import SearchIcon from '@mui/icons-material/Search'
import {
  InputAdornment,
  type OutlinedTextFieldProps,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, styled } from '@mui/material/styles'

import { fontSize, spacing } from '@/themes/tokens'

// A compact search control. It is sized and coloured for the app header, which is
// itself `background.paper`, so it fills with the page surface to read as recessed
// against its container rather than lifted off it. In light mode the two surfaces
// are nearly the same value and the border carries the definition.
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: 44,
    fontSize: fontSize.sm,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '& fieldset': { border: 'none' },
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(0, spacing.sm),
    // MUI dims the placeholder by fading `text.primary`, which leaves it washed
    // out against the recessed fill. Use the secondary text token at full strength
    // so it darkens in light mode and lightens in dark mode.
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
}))

/**
 * The leading magnifier is owned by the component rather than passed in, which is
 * why `slotProps` is not part of the public surface — it is the one prop this
 * would have to fight the caller over.
 *
 * Narrowed to the outlined member of TextField's variant union: the styling below
 * only targets `.MuiOutlinedInput-root`, so any other variant would render an
 * unstyled field. Because the notched outline is removed, a visible `label` isn't
 * available either — callers name the field with `aria-label` instead.
 */
export type SearchFieldProps = Omit<OutlinedTextFieldProps, 'slotProps' | 'variant'> & {
  /** Keyboard shortcut label shown at the trailing edge, e.g. `⌘+K`. */
  hint?: string
}

export const SearchField = ({ hint, 'aria-label': ariaLabel, ...props }: SearchFieldProps) => (
  <StyledTextField
    {...props}
    slotProps={{
      // TextField spreads its own props onto the wrapping form control, so the
      // accessible name has to be routed to the input element itself.
      htmlInput: { 'aria-label': ariaLabel },
      input: {
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon fontSize='small' />
          </InputAdornment>
        ),
        endAdornment: hint
          ? (
            // Pointer events off so clicking the hint still lands on the input,
            // and hidden from assistive tech so it isn't read as field content.
            <InputAdornment position='end' disablePointerEvents aria-hidden>
              <Typography variant='caption' color='text.secondary' noWrap>
                {hint}
              </Typography>
            </InputAdornment>
          )
          : undefined,
      },
    }}
  />
)
